/**
 * Modern header for fleet-doctor.
 * Inline free-text search, clickable notification dropdown,
 * and user card with Profile / Settings / prominent Sign Out.
 */
import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../app/modules/auth'
import { useGlobalFilter } from '../../app/shared/context/GlobalFilterContext'
import { clearVesselDetailCache } from '../../app/pages/dashboard/core/_requests'
import {
  Search,
  Bell,
  BellOff,
  Building,
  Building2,
  Layers,
  LogOut,
  Settings,
  ChevronDown,
  X,
  User,
  CheckCircle2,
  AlertCircle,
  Info,
  Ship,
} from 'lucide-react'

/* ─── Role name formatter ─── */
function formatRoleName(role: string | null | undefined): string {
  if (!role) return 'User'
  const map: Record<string, string> = {
    SUPER_ADMIN: 'Super Admin',
    COMPANY_ADMIN: 'Sub-Company Admin',
    COMPANY_GROUP_ADMIN: 'Company Admin',
    CREW: 'Crew',
    OPERATOR: 'Operator',
  }
  return map[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/* ─── Initials helper ─── */
function initialsFor(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

/* ─── Mock notification type ─── */
interface NotificationItem {
  id: string
  title: string
  description: string
  time: string
  type: 'success' | 'warning' | 'info'
  read: boolean
}

/* ─── Mock notifications (replace with real data later) ─── */
const MOCK_NOTIFICATIONS: NotificationItem[] = [
  {
    id: '1',
    title: 'PMS Job Completed',
    description: 'Main Engine overhaul scheduled for MT Oceanic completed.',
    time: '2 min ago',
    type: 'success',
    read: false,
  },
  {
    id: '2',
    title: 'Compliance Alert',
    description: 'Safety equipment inspection due for 3 vessels.',
    time: '1 hr ago',
    type: 'warning',
    read: false,
  },
  {
    id: '3',
    title: 'New Report Available',
    description: 'Monthly MARPOL report for May is ready for review.',
    time: '3 hr ago',
    type: 'info',
    read: true,
  },
  {
    id: '4',
    title: 'Bunker Operation',
    description: 'Bunker delivery completed at Port of Singapore.',
    time: '5 hr ago',
    type: 'success',
    read: true,
  },
]

const notificationIconMap = {
  success: CheckCircle2,
  warning: AlertCircle,
  info: Info,
}

const notificationColorMap = {
  success: 'text-emerald-600 bg-emerald-50',
  warning: 'text-amber-600 bg-amber-50',
  info: 'text-blue-600 bg-blue-50',
}

/* ─── Click Outside Hook ─── */
function useClickOutside<T extends HTMLElement>(callback: () => void) {
  const ref = useRef<T>(null)
  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        callback()
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [callback])
  return ref
}

/* ===================================================================
   Notification Dropdown
   =================================================================== */
function NotificationDropdown({ onClose }: { onClose: () => void }) {
  const ref = useClickOutside<HTMLDivElement>(onClose)
  const [notifications] = useState<NotificationItem[]>(MOCK_NOTIFICATIONS)
  const unreadCount = notifications.filter((n) => !n.read).length

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-[380px] bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.15)] border border-slate-200 z-50 overflow-hidden animate-dropdown-in"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-slate-700" />
          <span className="text-sm font-semibold text-slate-800">Notifications</span>
          {unreadCount > 0 && (
            <span className="text-[10px] font-bold text-white bg-blue-600 px-1.5 py-0.5 rounded-full leading-none">
              {unreadCount}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-md hover:bg-slate-100 transition-colors"
        >
          <X className="w-4 h-4 text-slate-400" />
        </button>
      </div>

      {/* List */}
      <div className="max-h-[340px] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-slate-400">
            <BellOff className="w-8 h-8 mb-2" />
            <p className="text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map((n) => {
            const Icon = notificationIconMap[n.type]
            const colorClass = notificationColorMap[n.type]
            return (
              <div
                key={n.id}
                className={`flex gap-3 px-5 py-3.5 cursor-pointer transition-colors hover:bg-slate-50 ${
                  !n.read ? 'bg-blue-50/40' : ''
                }`}
              >
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClass}`}
                >
                  <Icon className="w-4.5 h-4.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {n.title}
                    </p>
                    {!n.read && (
                      <span className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-1.5" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                    {n.description}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1 font-medium">
                    {n.time}
                  </p>
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-slate-100 px-5 py-3 text-center">
        <button className="text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors">
          View all notifications
        </button>
      </div>
    </div>
  )
}

/* ===================================================================
   User Dropdown — Profile / Settings / Sign Out
   =================================================================== */
function UserDropdown({
  userName,
  roleName,
  initials,
  onClose,
  onLogout,
}: {
  userName: string
  roleName: string
  initials: string
  onClose: () => void
  onLogout: () => void
}) {
  // navigate removed – unused
  const ref = useClickOutside<HTMLDivElement>(onClose)

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full mt-2 w-[260px] bg-white rounded-2xl shadow-[0_12px_48px_rgba(0,0,0,0.15)] border border-slate-200 z-50 overflow-hidden animate-dropdown-in"
    >
      {/* User info card at top */}
      <div className="px-5 pt-5 pb-4 bg-gradient-to-br from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-sm">
            {initials}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">
              {userName}
            </div>
            <div className="text-[11px] text-slate-400 font-medium">{roleName}</div>
          </div>
        </div>
      </div>

      {/* Menu items */}
      <div className="px-2 pb-2">
        <Link
          to="/manage/profile"
          onClick={onClose}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors"
        >
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <User className="w-4 h-4" />
          </div>
          <span className="font-medium">Profile</span>
        </Link>

        <Link
          to="/manage"
          onClick={onClose}
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-800 transition-colors"
        >
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <Settings className="w-4 h-4" />
          </div>
          <span className="font-medium">Settings</span>
        </Link>

        <div className="border-t border-slate-100 my-1" />

        <button
          type="button"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors text-left"
        >
          <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
            <LogOut className="w-4 h-4" />
          </div>
          <span className="font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  )
}

/* ===================================================================
   Main Header Component
   =================================================================== */
export default function Header() {
  const { currentUser, logout } = useAuth()
  const { state: filterState, companies, vessels, loading: filterLoading, setCompany, setVessel, setSearchQuery, clearFilters } = useGlobalFilter()
  const location = useLocation()
  const _navigate = useNavigate()

  const [searchQuery, setSearchQueryLocal] = useState(filterState.searchQuery || '')
  const [searchFocused, setSearchFocused] = useState(false)
  const [notifOpen, setNotifOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [companyOpen, setCompanyOpen] = useState(false)
  const [vesselOpen, setVesselOpen] = useState(false)
  const [companySearch, setCompanySearch] = useState('')
  const [vesselSearch, setVesselSearch] = useState('')

  const searchRef = useRef<HTMLDivElement>(null)
  const notifBtnRef = useRef<HTMLButtonElement>(null)
  const userBtnRef = useRef<HTMLButtonElement>(null)
  const companyRef = useRef<HTMLDivElement>(null)
  const vesselRef = useRef<HTMLDivElement>(null)

  // Sync local search state from context on mount / filter change
  useEffect(() => { setSearchQueryLocal(filterState.searchQuery || '') }, [filterState.searchQuery])

  const userName = currentUser?.username || 'User'
  const name = currentUser?.name || '--'
  const roleName = formatRoleName(currentUser?.role?.roleName)
  const initials = initialsFor(name !== '--' ? name : userName)

  /* ─── Page title map ─── */
  const pageMeta: Record<string, { title: string; subtitle: string }> = {
    '/dashboard': { title: 'Home', subtitle: 'Fleet Overview' },
    '/operations': { title: 'Operations', subtitle: 'Operations Dashboard' },
    '/crewing': { title: 'Crewing', subtitle: 'Crew Management' },
    '/manage': { title: 'Management', subtitle: 'System Management' },
    '/crewingtest': { title: 'Crewing', subtitle: 'Test Module' },
    '/qhse': { title: 'QHSE', subtitle: 'Quality, Health, Safety & Environment' },
    '/accounts': { title: 'Accounts', subtitle: 'Financial Management' },
    '/technical': { title: 'Technical', subtitle: 'Technical Management' },
    '/procurement': { title: 'Procurement', subtitle: 'Procurement Management' },
    '/builder': { title: 'Builder', subtitle: 'Layout Builder' },
    '/menu-test': { title: 'Menu Test', subtitle: 'Testing' },
  }
  const currentPath = '/' + location.pathname.split('/').filter(Boolean)[0]
  const meta = pageMeta[currentPath] || { title: 'Home', subtitle: 'Dashboard' }

  // Close dropdowns on outside click
  useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchFocused(false)
      if (companyRef.current && !companyRef.current.contains(e.target as Node)) {
        setCompanyOpen(false)
        setCompanySearch('')
      }
      if (vesselRef.current && !vesselRef.current.contains(e.target as Node)) {
        setVesselOpen(false)
        setVesselSearch('')
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [])

  // Close all on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setSearchFocused(false)
        setNotifOpen(false)
        setUserMenuOpen(false)
        setCompanyOpen(false)
        setCompanySearch('')
        setVesselOpen(false)
        setVesselSearch('')
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  function handleSignOut(): void {
    setUserMenuOpen(false)
    clearVesselDetailCache()  // clear cached vessel details between sessions
    logout()
  }

  function handleSearchChange(e: ChangeEvent<HTMLInputElement>): void {
    const val = e.target.value
    setSearchQueryLocal(val)
    setSearchQuery(val)
  }

  function handleSearchSubmit(e: React.FormEvent): void {
    e.preventDefault()
    if (searchQuery.trim()) {
      setSearchQuery(searchQuery.trim())
    }
  }

  const hasActiveFilters = filterState.company || filterState.vessel || filterState.searchQuery

  // Filtered lists for dropdown search
  const filteredCompanies = companies.filter((c) =>
    c.name.toLowerCase().includes(companySearch.toLowerCase())
  )
  const filteredVessels = vessels.filter((v) => {
    const name = ((v as any).fleet_name || (v as any).name || '').toLowerCase()
    return name.includes(vesselSearch.toLowerCase())
  })

  return (
    <header className="h-[68px] bg-white border-b border-slate-200 flex items-center gap-3 px-4 md:px-6 flex-shrink-0 z-20">
      {/* Left: Page Title */}
      <div className="flex-shrink-0 min-w-0 mr-1">
        <h1 className="text-lg font-bold leading-tight text-slate-900">
          {meta.title}
        </h1>
        <p className="text-[10px] text-slate-500 font-medium leading-tight">
          {meta.subtitle}
        </p>
      </div>

      {/* Company Filter Dropdown */}
      <div ref={companyRef} className="relative flex-shrink-0">
        <button
          type="button"
          onClick={() => { setCompanyOpen(!companyOpen); setVesselOpen(false) }}
          className={`flex items-center gap-1.5 h-9 px-3 rounded-xl text-left transition-all duration-200 text-xs whitespace-nowrap ${
            filterState.company
              ? 'bg-gradient-to-r from-blue-50 to-blue-100/50 text-blue-700 shadow-sm'
              : 'bg-white text-slate-600 hover:bg-slate-50 hover:shadow-sm'
          }`}
        >
          <Building className={`w-3.5 h-3.5 flex-shrink-0 ${filterState.company ? 'text-blue-500' : 'text-slate-400'}`} />
          <span className="font-medium truncate max-w-[140px]">
            {filterState.company ? filterState.company.name : 'All Companies'}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${filterState.company ? 'text-blue-400' : 'text-slate-400'} ${companyOpen ? 'rotate-180' : ''}`} />
        </button>
        {companyOpen && (
          <div className="absolute left-0 top-full mt-1.5 w-[320px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-slate-200/80 z-50 overflow-hidden animate-dropdown-in backdrop-blur-sm">
            {/* Search input */}
            <div className="px-4 pt-4 pb-3">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search companies…"
                  value={companySearch}
                  onChange={(e) => setCompanySearch(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:border-blue-400 focus:bg-white focus:ring-2 focus:ring-blue-100 transition-all"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-[280px] overflow-y-auto border-t border-slate-100">
              {/* Show All option */}
              <button
                type="button"
                className="w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors font-medium"
                onClick={() => { setCompany(null); setCompanyOpen(false); setCompanySearch('') }}
              >
                <Building className="w-4 h-4 text-slate-400" />
                <span>All Companies</span>
                {!filterState.company && <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 ml-auto" />}
              </button>
              {filteredCompanies.length === 0 ? (
                <div className="px-4 py-4 text-sm text-slate-400 text-center">
                  {companySearch ? 'No matching companies' : 'No companies available'}
                </div>
              ) : (
                <>
                  {/* Groups section */}
                  {filteredCompanies.some((c) => c.type === 'company_group') && (
                    <>
                      <div className="px-4 py-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Groups</span>
                      </div>
                      {filteredCompanies
                        .filter((c) => c.type === 'company_group')
                        .map((c) => (
                          <button
                            key={`group-${c.id}`}
                            type="button"
                            className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${
                              filterState.company?.id === c.id && filterState.company?.type === c.type
                                ? 'bg-blue-50 text-blue-700 font-medium border-l-[3px] border-l-blue-500'
                                : 'text-slate-600 hover:bg-slate-50 border-l-[3px] border-l-transparent'
                            }`}
                            onClick={() => { setCompany(c); setCompanyOpen(false); setCompanySearch('') }}
                          >
                            <Layers className="w-4 h-4 text-purple-400 flex-shrink-0" />
                            <span className="truncate">{c.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-purple-50 text-purple-600 font-medium ml-auto flex-shrink-0">Group</span>
                            {filterState.company?.id === c.id && filterState.company?.type === c.type && (
                              <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                    </>
                  )}
                  {/* Companies section */}
                  {filteredCompanies.some((c) => c.type === 'company_admin') && (
                    <>
                      <div className="px-4 pt-2 pb-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Companies</span>
                      </div>
                      {filteredCompanies
                        .filter((c) => c.type === 'company_admin')
                        .map((c) => (
                          <button
                            key={`company-${c.id}`}
                            type="button"
                            className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${
                              filterState.company?.id === c.id && filterState.company?.type === c.type
                                ? 'bg-blue-50 text-blue-700 font-medium border-l-[3px] border-l-blue-500'
                                : 'text-slate-600 hover:bg-slate-50 border-l-[3px] border-l-transparent'
                            }`}
                            onClick={() => { setCompany(c); setCompanyOpen(false); setCompanySearch('') }}
                          >
                            <Building2 className="w-4 h-4 text-sky-400 flex-shrink-0" />
                            <span className="truncate">{c.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-sky-50 text-sky-600 font-medium ml-auto flex-shrink-0">Company</span>
                            {filterState.company?.id === c.id && filterState.company?.type === c.type && (
                              <CheckCircle2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                            )}
                          </button>
                        ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Vessel Filter Dropdown */}
      <div ref={vesselRef} className="relative flex-shrink-0">
        <button
          type="button"
          onClick={() => { setVesselOpen(!vesselOpen); setCompanyOpen(false) }}
          className={`flex items-center gap-1.5 h-9 px-3 rounded-xl text-left transition-all duration-200 text-xs whitespace-nowrap ${
            filterState.vessel
              ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/50 text-emerald-700 shadow-sm'
              : 'bg-white text-slate-600 hover:bg-slate-50 hover:shadow-sm'
          }`}
        >
          <Ship className={`w-3.5 h-3.5 flex-shrink-0 ${filterState.vessel ? 'text-emerald-500' : 'text-slate-400'}`} />
          <span className="font-medium truncate max-w-[140px]">
            {filterState.vessel ? (filterState.vessel as any).fleet_name || (filterState.vessel as any).name || `Vessel #${filterState.vessel.id}` : 'All Vessels'}
          </span>
          <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200 ${filterState.vessel ? 'text-emerald-400' : 'text-slate-400'} ${vesselOpen ? 'rotate-180' : ''}`} />
        </button>
        {vesselOpen && (
          <div className="absolute left-0 top-full mt-1.5 w-[320px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.12)] border border-slate-200/80 z-50 overflow-hidden animate-dropdown-in backdrop-blur-sm">
            {/* Search input */}
            <div className="px-4 pt-4 pb-3">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search vessels…"
                  value={vesselSearch}
                  onChange={(e) => setVesselSearch(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 placeholder:text-slate-400 outline-none focus:border-emerald-400 focus:bg-white focus:ring-2 focus:ring-emerald-100 transition-all"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-[280px] overflow-y-auto border-t border-slate-100">
              {/* Show All option */}
              <button
                type="button"
                className="w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition-colors font-medium"
                onClick={() => { setVessel(null); setVesselOpen(false); setVesselSearch('') }}
              >
                <Ship className="w-4 h-4 text-slate-400" />
                <span>All Vessels</span>
                {!filterState.vessel && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto" />}
              </button>
              {filteredVessels.length === 0 ? (
                <div className="px-4 py-4 text-sm text-slate-400 text-center">
                  {vesselSearch ? 'No matching vessels' : filterState.company ? 'No vessels for this company' : 'No vessels available'}
                </div>
              ) : (
                filteredVessels.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    className={`w-full flex items-center gap-3 text-left px-4 py-2.5 text-sm transition-colors ${
                      filterState.vessel?.id === v.id
                        ? 'bg-emerald-50 text-emerald-700 font-medium border-l-[3px] border-l-emerald-500'
                        : 'text-slate-600 hover:bg-slate-50 border-l-[3px] border-l-transparent'
                    }`}
                    onClick={() => { setVessel(v); setVesselOpen(false); setVesselSearch('') }}
                  >
                    <Ship className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="truncate">{(v as any).fleet_name || (v as any).name || `Vessel #${v.id}`}</span>
                    {filterState.vessel?.id === v.id && (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 ml-auto" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Clear filters button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={() => { clearFilters(); setSearchQueryLocal('') }}
          className="flex items-center gap-1 h-8 px-2.5 rounded-lg text-[11px] font-medium text-rose-600 hover:bg-rose-50 hover:text-rose-700 transition-all flex-shrink-0"
          title="Clear all filters"
        >
          <X className="w-3 h-3" />
          Clear
        </button>
      )}

      {/* Center: Free-text Inline Search */}
      <div ref={searchRef} className="flex-1 flex justify-center max-w-lg mx-auto relative">
        {/* <form
          onSubmit={handleSearchSubmit}
          className={`w-full flex items-center gap-2 px-3.5 h-9 rounded-xl border transition-all duration-200 ${
            searchFocused
              ? 'border-blue-300 shadow-[0_0_0_3px_rgba(59,130,246,0.08)] bg-white'
              : 'border-slate-200 bg-slate-50 hover:bg-white hover:border-slate-300'
          }`}
        >
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search vessels, IMO, MMSI, ports, jobs..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setSearchFocused(true)}
            className="flex-1 bg-transparent border-none outline-none text-sm text-slate-700 placeholder:text-slate-400 min-w-0"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => { setSearchQueryLocal(''); setSearchQuery('') }}
              className="p-0.5 rounded hover:bg-slate-200 transition-colors"
            >
              <X className="w-3.5 h-3.5 text-slate-400" />
            </button>
          )}
        </form> */}

        {/* Search results dropdown */}
        {searchFocused && searchQuery.length > 0 && (
          <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl shadow-[0_16px_48px_rgba(0,0,0,0.1)] border border-slate-200/80 z-50 overflow-hidden animate-dropdown-in backdrop-blur-sm">
            <div className="px-4 py-3 border-b border-slate-100">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Press Enter to search current page
              </p>
            </div>
            <div className="py-2">
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 transition-colors text-left font-medium"
                onClick={handleSearchSubmit}
              >
                <Search className="w-4 h-4" />
                <span>Search for "{searchQuery}" on this page</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Notifications */}
        <div className="relative">
          <button
            ref={notifBtnRef}
            onClick={() => {
              setNotifOpen(!notifOpen)
              setUserMenuOpen(false)
            }}
            className="relative w-10 h-10 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center shadow-sm"
          >
            <Bell className="w-[18px] h-[18px] text-slate-600" />
            <span className="absolute top-2 right-2 w-2 h-2 bg-blue-600 rounded-full ring-2 ring-white notification-badge" />
          </button>
          {notifOpen && <NotificationDropdown onClose={() => setNotifOpen(false)} />}
        </div>

        {/* Divider */}
        <div className="hidden md:block w-px h-8 bg-slate-200 mx-1" />

        {/* User Card */}
        <div className="relative">
          <button
            ref={userBtnRef}
            onClick={() => {
              setUserMenuOpen(!userMenuOpen)
              setNotifOpen(false)
            }}
            className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm"
          >
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 shadow-sm">
              {initials}
            </div>
            <div className="hidden md:block min-w-0 text-left">
              <div className="text-sm font-semibold text-slate-700 truncate max-w-[130px] leading-tight">
                {name}
              </div>
              <div className="text-[10px] text-slate-400 font-medium truncate max-w-[130px] leading-tight">
                {roleName}
              </div>
            </div>
            <ChevronDown
              className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-200 ${
                userMenuOpen ? 'rotate-180' : ''
              }`}
            />
          </button>
          {userMenuOpen && (
            <UserDropdown
              userName={userName}
              roleName={roleName}
              initials={initials}
              onClose={() => setUserMenuOpen(false)}
              onLogout={handleSignOut}
            />
          )}
        </div>
      </div>
    </header>
  )
}
